import {applyAuthoritativeOverrides, applyRestrictiveOverrides} from './applyRestrictiveOverrides'
import type {BackendRolePermissions} from './permissions'
import type {ModulePermission} from './routeUtils'

export interface SignInPermissionTrees {
  /** The role's own tree (GET /roles/permissions). */
  roleTree?: ModulePermission[]
  /** The tree saved on the agency assignment the session starts in. */
  agencyTree?: ModulePermission[]
  /** The user's own top-level tree (`user.modulePermissions`). */
  userTree?: ModulePermission[]
}

const hasNodes = (tree: ModulePermission[] | undefined): tree is ModulePermission[] =>
  Array.isArray(tree) && tree.length > 0

/**
 * STFFLG-4243: the module permissions a session gets, by one rule for every place that stores them
 * (Signin and OAuthCallback at sign-in, RoleSelect, SelectAgencyMenu on an agency switch):
 *
 * 1. the role's tree is the base;
 * 2. the agency assignment's tree replaces the role's nodes it carries (applyAuthoritativeOverrides,
 *    STFFLG-2281);
 * 3. the user's own tree can only take away (applyRestrictiveOverrides).
 *
 * With no role tree — the lookup found no role, or the role has an empty tree — the answer is
 * `undefined`, never a tree built from the user's or the agency's tree alone. Each of the four used
 * to apply those verbatim there, so a grant stored on the user above their role (STFFLG-4243's
 * Settings) went live the moment the role lookup missed. Each caller keeps what it already did for a
 * user with no saved trees: no tree in the store, and the role's accessList where it has one.
 */
export function resolveEffectiveModulePermissions({
  roleTree,
  agencyTree,
  userTree,
}: SignInPermissionTrees): ModulePermission[] | undefined {
  if (!hasNodes(roleTree)) return undefined

  let permissions = roleTree
  if (hasNodes(agencyTree)) {
    permissions = applyAuthoritativeOverrides(permissions, agencyTree)
  }
  if (hasNodes(userTree)) {
    permissions = applyRestrictiveOverrides(permissions, userTree)
  }
  return permissions
}

/**
 * The one node of the tree a session holds when no role was found (noRolePermissions). Its key is
 * no module's, so nothing ever matches it.
 */
export const NO_ROLE_MODULE_KEY = 'no_role_found'

/**
 * STFFLG-4243: what a session holds when the role lookup finds no role for the user's role name (a
 * custom role it could not match, or a rename that left its users holding the old name): nothing.
 *
 * Storing no role would fail OPEN: with no role in the store the route guard lets every route
 * through (isRouteDeniedByPermissions) and the layout falls back to the role name, Platform for any
 * name it does not know. An empty tree would too: hasModulePermission (the route guard, the nav's
 * submodule items, My Signatures) allows everything on an empty tree. So the store gets a tree
 * holding one node no module is keyed by, granting nothing: every reader then answers by absence
 * (resolvePermission and the nav deny; the guard denies an absent node for a role that is not
 * Platform), with no accessList to fall back on, not Platform, and no category. A Settings page
 * answers by its absentDefault, as for any role lacking its row.
 */
export function noRolePermissions(roleName: string | undefined): BackendRolePermissions {
  return {
    _id: roleName ?? '',
    id: roleName ?? '',
    name: roleName ?? '',
    accessList: [],
    modulePermissions: [
      {
        key: NO_ROLE_MODULE_KEY,
        name: 'No role found',
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        submodules: [],
      },
    ],
    isPlatform: false,
    application: 'msp',
    createdDate: '',
    modifiedDate: '',
    versionKey: 0,
  }
}
